import { useState, useCallback, useRef, useEffect } from 'react'
import { Sparkles, Trash2, ChevronUp, ChevronDown, Eye, Download, Plus, Wand2, GripVertical, Layout, Type, Image, Box, Navigation, BarChart3, FileText, Layers, Copy, Edit3, Check, X } from 'lucide-react'
import DOMPurify from 'dompurify'
import './LightGrid.css'

const STORAGE_KEY = 'lightgrid-sections-v2'

// Component node - supports nesting
interface ComponentNode {
  id: string
  type: string
  category: string
  name: string
  props: Record<string, any>
  children: ComponentNode[]
  isContainer: boolean
}

interface Template {
  id: string
  name: string
  category: string
  icon: string
  isContainer: boolean
  defaultProps: Record<string, any>
}

const TEMPLATE_CATEGORIES = [
  { id: 'layout', name: 'Layout', icon: Layout, color: '#8b5cf6' },
  { id: 'ui', name: 'UI', icon: Box, color: '#f59e0b' },
  { id: 'text', name: 'Text', icon: Type, color: '#10b981' },
  { id: 'forms', name: 'Forms', icon: FileText, color: '#ec4899' },
  { id: 'media', name: 'Media', icon: Image, color: '#06b6d4' },
]

const TEMPLATES: Template[] = [
  // Layout - containers that can hold children
  { id: 'layout-section', name: 'Section', category: 'layout', icon: '📦', isContainer: true, defaultProps: { padding: '2rem', background: 'transparent' } },
  { id: 'layout-grid-2', name: '2 Column Grid', category: 'layout', icon: '▦', isContainer: true, defaultProps: { columns: 2, gap: '1rem' } },
  { id: 'layout-grid-3', name: '3 Column Grid', category: 'layout', icon: '▤', isContainer: true, defaultProps: { columns: 3, gap: '1rem' } },
  { id: 'layout-flex-row', name: 'Flex Row', category: 'layout', icon: '↔️', isContainer: true, defaultProps: { direction: 'row', gap: '1rem', align: 'center' } },
  { id: 'layout-flex-col', name: 'Flex Column', category: 'layout', icon: '↕️', isContainer: true, defaultProps: { direction: 'column', gap: '1rem' } },
  { id: 'layout-card', name: 'Card', category: 'layout', icon: '🃏', isContainer: true, defaultProps: { padding: '1.5rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.75rem' } },

  // UI - leaf components
  { id: 'ui-button', name: 'Button', category: 'ui', icon: '🔘', isContainer: false, defaultProps: { text: 'Click Me', variant: 'primary' } },
  { id: 'ui-badge', name: 'Badge', category: 'ui', icon: '🏷️', isContainer: false, defaultProps: { text: 'Badge', color: 'cyan' } },
  { id: 'ui-avatar', name: 'Avatar', category: 'ui', icon: '👤', isContainer: false, defaultProps: { size: '48px', src: '' } },
  { id: 'ui-icon', name: 'Icon', category: 'ui', icon: '⭐', isContainer: false, defaultProps: { icon: 'star', size: '24px' } },
  { id: 'ui-divider', name: 'Divider', category: 'ui', icon: '➖', isContainer: false, defaultProps: { color: 'rgba(255,255,255,0.1)' } },

  // Text - content components
  { id: 'text-heading', name: 'Heading', category: 'text', icon: 'H1', isContainer: false, defaultProps: { text: 'Heading', level: 'h2', align: 'left' } },
  { id: 'text-paragraph', name: 'Paragraph', category: 'text', icon: '¶', isContainer: false, defaultProps: { text: 'Enter your text here...', align: 'left' } },
  { id: 'text-link', name: 'Link', category: 'text', icon: '🔗', isContainer: false, defaultProps: { text: 'Click here', href: '#', color: 'cyan' } },
  { id: 'text-list', name: 'List', category: 'text', icon: '📝', isContainer: false, defaultProps: { items: ['Item 1', 'Item 2', 'Item 3'], ordered: false } },

  // Forms
  { id: 'form-input', name: 'Input', category: 'forms', icon: '📝', isContainer: false, defaultProps: { placeholder: 'Enter text...', label: 'Label' } },
  { id: 'form-textarea', name: 'Textarea', category: 'forms', icon: '📄', isContainer: false, defaultProps: { placeholder: 'Enter text...', label: 'Label', rows: 4 } },
  { id: 'form-select', name: 'Select', category: 'forms', icon: '▼', isContainer: false, defaultProps: { label: 'Select', options: ['Option 1', 'Option 2', 'Option 3'] } },
  { id: 'form-checkbox', name: 'Checkbox', category: 'forms', icon: '☑️', isContainer: false, defaultProps: { label: 'Check me', checked: false } },

  // Media
  { id: 'media-image', name: 'Image', category: 'media', icon: '🖼️', isContainer: false, defaultProps: { src: 'https://placehold.co/400x300', alt: 'Image', width: '100%' } },
  { id: 'media-video', name: 'Video', category: 'media', icon: '🎬', isContainer: false, defaultProps: { src: '', poster: '' } },
  { id: 'media-spacer', name: 'Spacer', category: 'media', icon: '⬜', isContainer: false, defaultProps: { height: '2rem' } },
]

const generateId = () => `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

// Render a component node to JSX string
const renderNodeToCode = (node: ComponentNode, indent = 0): string => {
  const spaces = '  '.repeat(indent)
  const { type, props, children } = node

  switch (type) {
    case 'layout-section':
      return `${spaces}<section style={{ padding: '${props.padding}', background: '${props.background}' }}>\n${children.map(c => renderNodeToCode(c, indent + 1)).join('\n')}\n${spaces}</section>`
    case 'layout-grid-2':
    case 'layout-grid-3':
      return `${spaces}<div style={{ display: 'grid', gridTemplateColumns: 'repeat(${props.columns}, 1fr)', gap: '${props.gap}' }}>\n${children.map(c => renderNodeToCode(c, indent + 1)).join('\n')}\n${spaces}</div>`
    case 'layout-flex-row':
    case 'layout-flex-col':
      return `${spaces}<div style={{ display: 'flex', flexDirection: '${props.direction}', gap: '${props.gap}', alignItems: '${props.align || 'stretch'}' }}>\n${children.map(c => renderNodeToCode(c, indent + 1)).join('\n')}\n${spaces}</div>`
    case 'layout-card':
      return `${spaces}<div style={{ padding: '${props.padding}', background: '${props.background}', border: '${props.border}', borderRadius: '${props.borderRadius}' }}>\n${children.map(c => renderNodeToCode(c, indent + 1)).join('\n')}\n${spaces}</div>`
    case 'text-heading':
      return `${spaces}<${props.level} style={{ textAlign: '${props.align}' }}>${props.text}</${props.level}>`
    case 'text-paragraph':
      return `${spaces}<p style={{ textAlign: '${props.align}' }}>${props.text}</p>`
    case 'text-link':
      return `${spaces}<a href="${props.href}" style={{ color: '${props.color}' }}>${props.text}</a>`
    case 'ui-button':
      return `${spaces}<button className="btn btn-${props.variant}">${props.text}</button>`
    case 'ui-badge':
      return `${spaces}<span className="badge" style={{ background: '${props.color}' }}>${props.text}</span>`
    case 'ui-divider':
      return `${spaces}<hr style={{ borderColor: '${props.color}' }} />`
    case 'form-input':
      return `${spaces}<div>\n${spaces}  <label>${props.label}</label>\n${spaces}  <input placeholder="${props.placeholder}" />\n${spaces}</div>`
    case 'media-image':
      return `${spaces}<img src="${props.src}" alt="${props.alt}" style={{ width: '${props.width}' }} />`
    case 'media-spacer':
      return `${spaces}<div style={{ height: '${props.height}' }} />`
    default:
      return `${spaces}<div>${node.name}</div>`
  }
}

export function LightGrid() {
  const [nodes, setNodes] = useState<ComponentNode[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState<string>('layout')
  const [previewMode, setPreviewMode] = useState(false)
  const [draggedTemplate, setDraggedTemplate] = useState<Template | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [editingProp, setEditingProp] = useState<{ nodeId: string; prop: string } | null>(null)

  // Load from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        setNodes(JSON.parse(stored))
      }
    } catch (e) {
      console.warn('Failed to load from localStorage')
    }
  }, [])

  // Save to localStorage
  useEffect(() => {
    if (nodes.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(nodes))
      } catch (e) {
        console.warn('Failed to save to localStorage')
      }
    }
  }, [nodes])

  const findNodeById = useCallback((id: string, nodeList: ComponentNode[] = nodes): ComponentNode | null => {
    for (const node of nodeList) {
      if (node.id === id) return node
      const found = findNodeById(id, node.children)
      if (found) return found
    }
    return null
  }, [nodes])

  const findParentNode = useCallback((id: string, nodeList: ComponentNode[] = nodes, parent: ComponentNode | null = null): ComponentNode | null => {
    for (const node of nodeList) {
      if (node.id === id) return parent
      const found = findParentNode(id, node.children, node)
      if (found !== undefined) return found
    }
    return null
  }, [nodes])

  const addNodeToRoot = useCallback((template: Template) => {
    const newNode: ComponentNode = {
      id: generateId(),
      type: template.id,
      category: template.category,
      name: template.name,
      props: { ...template.defaultProps },
      children: [],
      isContainer: template.isContainer,
    }
    setNodes(prev => [...prev, newNode])
    setSelectedId(newNode.id)
  }, [])

  const addNodeToParent = useCallback((parentId: string, template: Template) => {
    const newNode: ComponentNode = {
      id: generateId(),
      type: template.id,
      category: template.category,
      name: template.name,
      props: { ...template.defaultProps },
      children: [],
      isContainer: template.isContainer,
    }

    const updateChildren = (nodeList: ComponentNode[]): ComponentNode[] => {
      return nodeList.map(node => {
        if (node.id === parentId) {
          return { ...node, children: [...node.children, newNode] }
        }
        return { ...node, children: updateChildren(node.children) }
      })
    }

    setNodes(prev => updateChildren(prev))
    setSelectedId(newNode.id)
  }, [])

  const removeNode = useCallback((id: string) => {
    const removeFromList = (nodeList: ComponentNode[]): ComponentNode[] => {
      return nodeList.filter(node => node.id !== id).map(node => ({
        ...node,
        children: removeFromList(node.children)
      }))
    }
    setNodes(prev => removeFromList(prev))
    if (selectedId === id) setSelectedId(null)
  }, [selectedId])

  const duplicateNode = useCallback((id: string) => {
    const cloneNode = (node: ComponentNode): ComponentNode => ({
      ...node,
      id: generateId(),
      name: `${node.name} (copy)`,
      children: node.children.map(cloneNode)
    })

    const duplicateInList = (nodeList: ComponentNode[]): ComponentNode[] => {
      const result: ComponentNode[] = []
      for (const node of nodeList) {
        result.push({ ...node, children: duplicateInList(node.children) })
        if (node.id === id) {
          result.push(cloneNode(node))
        }
      }
      return result
    }

    setNodes(prev => duplicateInList(prev))
  }, [])

  const updateNodeProps = useCallback((id: string, newProps: Record<string, any>) => {
    const updateInList = (nodeList: ComponentNode[]): ComponentNode[] => {
      return nodeList.map(node => {
        if (node.id === id) {
          return { ...node, props: { ...node.props, ...newProps } }
        }
        return { ...node, children: updateInList(node.children) }
      })
    }
    setNodes(prev => updateInList(prev))
  }, [])

  const moveNode = useCallback((id: string, direction: 'up' | 'down') => {
    const moveInList = (nodeList: ComponentNode[]): ComponentNode[] => {
      const index = nodeList.findIndex(n => n.id === id)
      if (index === -1) {
        return nodeList.map(node => ({ ...node, children: moveInList(node.children) }))
      }
      if (direction === 'up' && index === 0) return nodeList
      if (direction === 'down' && index === nodeList.length - 1) return nodeList
      const newList = [...nodeList]
      const swapIndex = direction === 'up' ? index - 1 : index + 1
      ;[newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]]
      return newList
    }
    setNodes(prev => moveInList(prev))
  }, [])

  // Handle drag & drop
  const handleDragStart = (e: React.DragEvent, template: Template) => {
    setDraggedTemplate(template)
    e.dataTransfer.effectAllowed = 'copy'
  }

  const handleDragOver = (e: React.DragEvent, targetId: string | null) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverId(targetId)
  }

  const handleDrop = (e: React.DragEvent, targetId: string | null) => {
    e.preventDefault()
    e.stopPropagation()
    if (!draggedTemplate) return

    if (targetId) {
      const targetNode = findNodeById(targetId)
      if (targetNode?.isContainer) {
        addNodeToParent(targetId, draggedTemplate)
      } else {
        addNodeToRoot(draggedTemplate)
      }
    } else {
      addNodeToRoot(draggedTemplate)
    }

    setDraggedTemplate(null)
    setDragOverId(null)
  }

  const handleDragEnd = () => {
    setDraggedTemplate(null)
    setDragOverId(null)
  }

  // Export to code
  const exportCode = () => {
    const code = nodes.map(n => renderNodeToCode(n)).join('\n\n')
    const fullCode = `export default function GeneratedPage() {\n  return (\n    <div className="generated-page">\n${code}\n    </div>\n  )\n}`

    const blob = new Blob([fullCode], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'GeneratedPage.tsx'
    a.click()
    URL.revokeObjectURL(url)
  }

  // Render a node in the canvas
  const renderCanvasNode = (node: ComponentNode, depth = 0): JSX.Element => {
    const isSelected = selectedId === node.id
    const isDragOver = dragOverId === node.id

    return (
      <div
        key={node.id}
        className={`canvas-node ${isSelected ? 'selected' : ''} ${isDragOver && node.isContainer ? 'drag-over' : ''} ${node.isContainer ? 'container' : 'leaf'}`}
        onClick={(e) => { e.stopPropagation(); setSelectedId(node.id) }}
        onDragOver={(e) => node.isContainer ? handleDragOver(e, node.id) : undefined}
        onDrop={(e) => node.isContainer ? handleDrop(e, node.id) : undefined}
      >
        <div className="node-header">
          <span className="node-icon">{TEMPLATES.find(t => t.id === node.type)?.icon || '📦'}</span>
          <span className="node-name">{node.name}</span>
          {node.isContainer && <span className="container-badge">Container</span>}
          <div className="node-actions">
            <button onClick={(e) => { e.stopPropagation(); moveNode(node.id, 'up') }} title="Move up">
              <ChevronUp className="w-3 h-3" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); moveNode(node.id, 'down') }} title="Move down">
              <ChevronDown className="w-3 h-3" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); duplicateNode(node.id) }} title="Duplicate" className="duplicate-btn">
              <Copy className="w-3 h-3" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); removeNode(node.id) }} title="Delete" className="delete-btn">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Inline content editing for text nodes */}
        {node.type.startsWith('text-') && (
          <div className="node-content-preview">
            {editingProp?.nodeId === node.id && editingProp.prop === 'text' ? (
              <input
                autoFocus
                value={node.props.text || ''}
                onChange={(e) => updateNodeProps(node.id, { text: e.target.value })}
                onBlur={() => setEditingProp(null)}
                onKeyDown={(e) => e.key === 'Enter' && setEditingProp(null)}
                className="inline-edit-input"
              />
            ) : (
              <span
                className="editable-text"
                onClick={(e) => { e.stopPropagation(); setEditingProp({ nodeId: node.id, prop: 'text' }) }}
              >
                {node.props.text || 'Click to edit'}
                <Edit3 className="w-3 h-3 edit-icon" />
              </span>
            )}
          </div>
        )}

        {/* Render children for containers */}
        {node.isContainer && (
          <div className="node-children">
            {node.children.length === 0 ? (
              <div
                className="drop-zone-empty"
                onDragOver={(e) => handleDragOver(e, node.id)}
                onDrop={(e) => handleDrop(e, node.id)}
              >
                <Plus className="w-4 h-4" />
                <span>Drop components here</span>
              </div>
            ) : (
              node.children.map(child => renderCanvasNode(child, depth + 1))
            )}
          </div>
        )}
      </div>
    )
  }

  // Render preview
  const renderPreviewNode = (node: ComponentNode): JSX.Element => {
    const { type, props, children } = node
    const childElements = children.map(renderPreviewNode)

    switch (type) {
      case 'layout-section':
        return <section key={node.id} style={{ padding: props.padding, background: props.background }}>{childElements}</section>
      case 'layout-grid-2':
      case 'layout-grid-3':
        return <div key={node.id} style={{ display: 'grid', gridTemplateColumns: `repeat(${props.columns}, 1fr)`, gap: props.gap }}>{childElements}</div>
      case 'layout-flex-row':
      case 'layout-flex-col':
        return <div key={node.id} style={{ display: 'flex', flexDirection: props.direction, gap: props.gap, alignItems: props.align }}>{childElements}</div>
      case 'layout-card':
        return <div key={node.id} style={{ padding: props.padding, background: props.background, border: props.border, borderRadius: props.borderRadius }}>{childElements}</div>
      case 'text-heading':
        const HeadingTag = props.level as keyof JSX.IntrinsicElements
        return <HeadingTag key={node.id} style={{ textAlign: props.align, color: 'white' }}>{props.text}</HeadingTag>
      case 'text-paragraph':
        return <p key={node.id} style={{ textAlign: props.align, color: 'rgba(255,255,255,0.8)' }}>{props.text}</p>
      case 'text-link':
        return <a key={node.id} href={props.href} style={{ color: props.color }}>{props.text}</a>
      case 'ui-button':
        return <button key={node.id} style={{ padding: '0.5rem 1rem', background: props.variant === 'primary' ? '#06b6d4' : 'transparent', border: '1px solid #06b6d4', borderRadius: '0.5rem', color: 'white', cursor: 'pointer' }}>{props.text}</button>
      case 'ui-badge':
        return <span key={node.id} style={{ padding: '0.25rem 0.5rem', background: props.color, borderRadius: '9999px', fontSize: '0.75rem', color: 'white' }}>{props.text}</span>
      case 'ui-divider':
        return <hr key={node.id} style={{ border: 'none', borderTop: `1px solid ${props.color}`, margin: '1rem 0' }} />
      case 'form-input':
        return <div key={node.id} style={{ marginBottom: '1rem' }}><label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255,255,255,0.7)' }}>{props.label}</label><input placeholder={props.placeholder} style={{ width: '100%', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '0.5rem', color: 'white' }} /></div>
      case 'media-image':
        return <img key={node.id} src={props.src} alt={props.alt} style={{ width: props.width, maxWidth: '100%' }} />
      case 'media-spacer':
        return <div key={node.id} style={{ height: props.height }} />
      default:
        return <div key={node.id}>{node.name}</div>
    }
  }

  const selectedNode = selectedId ? findNodeById(selectedId) : null
  const filteredTemplates = TEMPLATES.filter(t => t.category === activeCategory)

  // Preview mode
  if (previewMode) {
    return (
      <div className="lightgrid-preview">
        <div className="preview-toolbar">
          <button onClick={() => setPreviewMode(false)} className="preview-exit">← Back to Editor</button>
          <span className="preview-title">Live Preview</span>
        </div>
        <div className="preview-frame">
          {nodes.length === 0 ? (
            <div className="preview-empty">
              <p>No components to preview</p>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>Add components from the palette</p>
            </div>
          ) : (
            <div className="preview-content">
              {nodes.map(renderPreviewNode)}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="lightgrid">
      {/* Component Palette */}
      <div className="lightgrid-palette">
        <div className="palette-header">
          <Layers className="w-5 h-5" />
          <span>Components</span>
        </div>

        {/* Category Tabs */}
        <div className="palette-categories">
          {TEMPLATE_CATEGORIES.map(cat => {
            const Icon = cat.icon
            return (
              <button
                key={cat.id}
                className={`category-tab ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
                style={{ '--cat-color': cat.color } as React.CSSProperties}
                title={cat.name}
              >
                <Icon className="w-4 h-4" />
              </button>
            )
          })}
        </div>

        {/* Template List */}
        <div className="template-list">
          {filteredTemplates.map(template => (
            <div
              key={template.id}
              className="template-item"
              draggable
              onDragStart={(e) => handleDragStart(e, template)}
              onDragEnd={handleDragEnd}
            >
              <span className="template-icon">{template.icon}</span>
              <span className="template-name">{template.name}</span>
              {template.isContainer && <span className="template-container-tag">⊞</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Canvas */}
      <div className="lightgrid-canvas">
        <div className="canvas-toolbar">
          <span className="toolbar-title">Canvas</span>
          <div className="toolbar-actions">
            <button onClick={() => setPreviewMode(true)} className="toolbar-btn">
              <Eye className="w-4 h-4" /> Preview
            </button>
            <button onClick={exportCode} className="toolbar-btn">
              <Download className="w-4 h-4" /> Export
            </button>
            <button onClick={() => { setNodes([]); localStorage.removeItem(STORAGE_KEY) }} className="toolbar-btn danger">
              <Trash2 className="w-4 h-4" /> Clear
            </button>
          </div>
        </div>

        <div
          className={`canvas-content ${draggedTemplate ? 'dragging' : ''}`}
          onDragOver={(e) => handleDragOver(e, null)}
          onDrop={(e) => handleDrop(e, null)}
        >
          {nodes.length === 0 ? (
            <div className="canvas-empty">
              <Wand2 className="w-12 h-12" />
              <p>Drag components here to start building</p>
              <p className="hint">Containers can hold other components</p>
            </div>
          ) : (
            nodes.map(node => renderCanvasNode(node))
          )}
        </div>
      </div>

      {/* Inspector */}
      <div className="lightgrid-inspector">
        <div className="inspector-header">
          <span>Properties</span>
        </div>

        {selectedNode ? (
          <div className="inspector-content">
            <div className="inspector-section">
              <label>Component</label>
              <div className="inspector-value">{selectedNode.name}</div>
            </div>

            <div className="inspector-section">
              <label>Type</label>
              <div className="inspector-value type-badge">{selectedNode.type}</div>
            </div>

            <div className="inspector-divider" />

            <div className="inspector-section">
              <label>Properties</label>
              {Object.entries(selectedNode.props).map(([key, value]) => (
                <div key={key} className="prop-row">
                  <span className="prop-key">{key}</span>
                  {typeof value === 'boolean' ? (
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => updateNodeProps(selectedNode.id, { [key]: e.target.checked })}
                    />
                  ) : Array.isArray(value) ? (
                    <textarea
                      value={value.join('\n')}
                      onChange={(e) => updateNodeProps(selectedNode.id, { [key]: e.target.value.split('\n') })}
                      className="prop-input"
                      rows={3}
                    />
                  ) : (
                    <input
                      type="text"
                      value={String(value)}
                      onChange={(e) => updateNodeProps(selectedNode.id, { [key]: e.target.value })}
                      className="prop-input"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="inspector-empty">
            <p>Select a component to edit its properties</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default LightGrid
